/**
 * UsagePanel — Owner / Admin / Platform-root only. Restrained read-only
 * surface that polls /api/usage/summary and renders:
 *
 *   • Plan + activation status badge (with trial-days-remaining when applicable)
 *   • A calm progress row per metric (green < 70%, amber ≥ 80%, red ≥ 100%)
 *   • Provider attempts breakdown (admin-only): OpenAI / Anthropic / Gemini
 *     attempts, successes, failures, insufficient_quota count, fallback used
 *   • One-line warnings at 80% and 100% thresholds
 *
 * Failure mode: if the endpoint 401/403s, the panel renders nothing
 * (Operators / Viewers should never see provider internals).
 */
import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const METRIC_LABELS = {
  scout_scans_per_day: 'Scout scans (today)',
  llm_attempts_per_day: 'LLM attempts (today)',
  fix_generations_per_day: 'Fix generations (today)',
  fit_generations_per_day: 'Fit generations (today)',
  saved_investigations_total: 'Saved investigations',
  tracker_runs_per_day: 'Tracker runs (today)',
};

function tone(percent) {
  if (percent === null || percent === undefined) return '#16a34a'; // unlimited
  if (percent >= 100) return '#b91c1c';
  if (percent >= 80) return '#b45309';
  if (percent >= 70) return '#d97706';
  return '#16a34a';
}

function MetricRow({ m }) {
  const pctText = m.limit === null
    ? 'unlimited'
    : m.limit === 0
      ? 'blocked'
      : `${m.used} / ${m.limit}`;
  const barPercent = (m.percent === null || m.percent === undefined) ? 0 : Math.min(100, m.percent);
  const barColor = tone(m.percent);

  return (
    <div data-testid={`usage-metric-${m.metric}`} style={{
      display: 'grid', gridTemplateColumns: '1fr auto', gap: 8,
      padding: '8px 0', borderBottom: '1px solid var(--border-subtle, #eee)',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
          <span style={{ fontWeight: 500 }}>{METRIC_LABELS[m.metric] || m.metric}</span>
          <span style={{ color: 'var(--text-secondary)' }}>{pctText}</span>
        </div>
        {m.limit !== null && m.limit > 0 && (
          <div aria-hidden style={{
            height: 4, borderRadius: 2, background: 'var(--bg-subtle, #f1f5f9)',
            overflow: 'hidden',
          }}>
            <div style={{ width: `${barPercent}%`, height: '100%', background: barColor }} />
          </div>
        )}
      </div>
    </div>
  );
}

function ProviderBreakdown({ providers }) {
  if (!providers) return null;
  const rows = ['openai', 'anthropic', 'gemini', 'deterministic'].map((p) => {
    const data = providers[p] || {};
    return { name: p, ...data };
  });
  // Hide entirely if no provider activity at all today.
  const hasActivity = rows.some((r) => (r.attempts || 0) > 0)
    || providers.fallback_used_count > 0
    || providers.degraded_runs > 0;
  if (!hasActivity) return null;

  const fmtCost = (v) => v == null ? '—' : `$${Number(v).toFixed(4)}`;
  const fmtTok  = (v) => v == null ? '—' : Number(v).toLocaleString();

  return (
    <div data-testid="usage-providers" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle, #e5e7eb)' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
        Provider attempts (today)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr repeat(6, 1fr)', gap: 4, fontSize: 11 }}>
        <span style={{ color: 'var(--text-secondary)' }}>provider</span>
        <span style={{ color: 'var(--text-secondary)', textAlign: 'right' }}>attempts</span>
        <span style={{ color: 'var(--text-secondary)', textAlign: 'right' }}>ok</span>
        <span style={{ color: 'var(--text-secondary)', textAlign: 'right' }}>failed</span>
        <span style={{ color: 'var(--text-secondary)', textAlign: 'right' }}>quota</span>
        <span style={{ color: 'var(--text-secondary)', textAlign: 'right' }}>tokens</span>
        <span style={{ color: 'var(--text-secondary)', textAlign: 'right' }}>est. cost</span>
        {rows.filter((r) => (r.attempts || 0) > 0).map((r) => {
          const tokens = (r.input_tokens == null && r.output_tokens == null)
            ? null
            : ((r.input_tokens || 0) + (r.output_tokens || 0));
          return (
            <React.Fragment key={r.name}>
              <span style={{ fontWeight: 500 }} data-testid={`usage-provider-${r.name}`}>{r.name}</span>
              <span style={{ textAlign: 'right' }}>{r.attempts || 0}</span>
              <span style={{ textAlign: 'right', color: '#15803d' }}>{r.successes || 0}</span>
              <span style={{ textAlign: 'right', color: r.failures ? '#b45309' : 'inherit' }}>{r.failures || 0}</span>
              <span style={{ textAlign: 'right', color: r.insufficient_quota ? '#b91c1c' : 'inherit' }}>{r.insufficient_quota || 0}</span>
              <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{fmtTok(tokens)}</span>
              <span style={{ textAlign: 'right' }}>{fmtCost(r.est_cost_usd)}</span>
            </React.Fragment>
          );
        })}
      </div>
      {(providers.fallback_used_count > 0 || providers.degraded_runs > 0) && (
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
          Fallback used: {providers.fallback_used_count} ·{' '}
          Degraded runs: {providers.degraded_runs}
        </div>
      )}
    </div>
  );
}

function TokenCostBlock({ today, month }) {
  if (!today && !month) return null;
  const fmt = (v) => v == null ? '—' : Number(v).toLocaleString();
  const fmtCost = (v) => v == null || v === 0 ? '—' : `$${Number(v).toFixed(4)}`;
  const showToday = today && (today.llm_attempts > 0 || today.input_tokens > 0);
  const showMonth = month && (month.llm_attempts > 0 || month.input_tokens > 0);
  if (!showToday && !showMonth) return null;

  // Cost-completeness widget — only meaningful when we have at least one
  // successful LLM call today. Calm tone: never red. Hints why the figure
  // may be partial without exposing provider internals.
  let completenessNode = null;
  if (today && Number.isFinite(today.cost_completeness_percent)) {
    const pct = today.cost_completeness_percent;
    const tone = pct >= 95 ? '#15803d' : pct >= 70 ? '#475569' : '#b45309';
    completenessNode = (
      <div data-testid="usage-cost-completeness" style={{
        marginTop: 8, fontSize: 11, color: tone,
      }}>
        Cost completeness: <strong>{pct}%</strong>
        <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>
          {pct >= 100
            ? 'Estimated cost is based on available provider usage metadata.'
            : 'Some provider responses did not include full usage metadata, so estimated cost may be partial.'}
        </span>
      </div>
    );
  } else if (today && (today.llm_attempts || 0) > 0) {
    // Successful calls exist but completeness can't be computed (e.g. all
    // failed). Surface a calm "unavailable" rather than hiding the line.
    completenessNode = (
      <div data-testid="usage-cost-completeness-unavailable" style={{
        marginTop: 8, fontSize: 11, color: 'var(--text-secondary)',
      }}>
        Cost completeness unavailable
      </div>
    );
  }

  return (
    <div data-testid="usage-token-cost-block" style={{
      marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle, #e5e7eb)',
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16,
    }}>
      {showToday && (
        <div data-testid="usage-token-cost-today">
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
            Today
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.7 }}>
            <div>LLM attempts: <strong>{today.llm_attempts || 0}</strong></div>
            <div>Input tokens: <strong>{fmt(today.input_tokens)}</strong></div>
            <div>Output tokens: <strong>{fmt(today.output_tokens)}</strong></div>
            <div>
              Estimated provider cost: <strong data-testid="usage-cost-today">{fmtCost(today.est_cost_usd)}</strong>
              {today.cost_is_partial && (
                <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-secondary)' }}>
                  (partial — some responses lacked usage metadata)
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      {showMonth && (
        <div data-testid="usage-token-cost-month">
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
            Month-to-date
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.7 }}>
            <div>LLM attempts: <strong>{month.llm_attempts || 0}</strong></div>
            <div>Input tokens: <strong>{fmt(month.input_tokens)}</strong></div>
            <div>Output tokens: <strong>{fmt(month.output_tokens)}</strong></div>
            <div>
              Estimated provider cost: <strong data-testid="usage-cost-month">{fmtCost(month.est_cost_usd)}</strong>
            </div>
          </div>
        </div>
      )}
      <div style={{ gridColumn: '1 / -1', fontSize: 10, color: 'var(--text-secondary)' }}>
        Token and cost figures are estimates based on provider response metadata. Not a billing statement.
        {completenessNode}
      </div>
    </div>
  );
}

export default function UsagePanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/usage/summary`);
      if (res.status === 401 || res.status === 403) {
        // Hide silently for non-admin viewers.
        setError('forbidden'); return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { load(); }, []);
  if (error === 'forbidden') return null;

  const metrics = data?.today?.metrics || [];
  const approaching = metrics.filter((m) => m.warning === 'approaching_limit');
  const reached = metrics.filter((m) => m.warning === 'limit_reached');

  // Calm one-line status — uses Trial days + scout-percent when both exist.
  const oneLiner = (() => {
    if (!data) return null;
    const scout = metrics.find((m) => m.metric === 'scout_scans_per_day');
    const parts = [];
    if (data.activation_status === 'trial' && Number.isFinite(data.days_remaining)) {
      parts.push(`${data.days_remaining} day${data.days_remaining === 1 ? '' : 's'} of trial left`);
    }
    if (scout && scout.percent !== null && scout.percent !== undefined && scout.limit) {
      parts.push(`${Math.round(scout.percent)}% of today's Scout limit used`);
    }
    return parts.length ? parts.join('. ') + '.' : null;
  })();

  return (
    <div className="panel" style={{ marginBottom: 16 }} data-testid="usage-panel">
      <div className="panel-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', margin: 0 }}>
            Usage &amp; limits
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={load} data-testid="usage-refresh">
            Refresh
          </button>
        </div>
        {oneLiner && (
          <p data-testid="usage-one-liner" style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 10px' }}>
            {oneLiner}
          </p>
        )}

        {/* Calm warnings — only render when something actually warrants attention. */}
        {reached.length > 0 && (
          <div data-testid="usage-warning-reached" style={{
            margin: '4px 0 8px', padding: '8px 10px', borderRadius: 4,
            background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 12,
          }}>
            {reached.map((m) => (
              <div key={m.metric}>
                {METRIC_LABELS[m.metric] || m.metric} limit reached for today.
                {m.reset_at && ` Resets at ${new Date(m.reset_at).toLocaleTimeString()}.`}
              </div>
            ))}
          </div>
        )}
        {approaching.length > 0 && reached.length === 0 && (
          <div data-testid="usage-warning-approaching" style={{
            margin: '4px 0 8px', padding: '8px 10px', borderRadius: 4,
            background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e', fontSize: 12,
          }}>
            {approaching.map((m) => (
              <div key={m.metric}>
                {METRIC_LABELS[m.metric] || m.metric} is approaching today's limit ({m.used} / {m.limit}).
              </div>
            ))}
          </div>
        )}

        {error && error !== 'forbidden' && (
          <div style={{ fontSize: 12, color: 'var(--danger, #dc2626)' }}>{error}</div>
        )}

        {data && metrics.map((m) => <MetricRow key={m.metric} m={m} />)}

        <TokenCostBlock today={data?.today?.llm_totals} month={data?.month} />

        <ProviderBreakdown providers={data?.providers} />
      </div>
    </div>
  );
}
